import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@trillion/ui/lib/api-client";
import type { BoardDocument, DiagramCheckpoint } from "../types/board";
import { compareRevision } from "./diagram-collaboration";
import { canonicalDocument, persistableSnapshot } from "./board-document";

type Operation = "save" | "restore" | "reload";
export interface SaveFailure { kind: "failed" | "conflict" | "restore"; message: string }

export function saveFailure(error: unknown, uncertainRestore = false): SaveFailure {
  if (error instanceof ApiError && error.status === 409) {
    try {
      if (JSON.parse(error.body).error === "diagram_conflict") return { kind: "conflict",
        message: "This diagram changed on the server. Download your local copy, then reload the latest version before reapplying your edits." };
    } catch { /* Non-JSON failures remain ordinary errors. */ }
  }
  return uncertainRestore ? { kind: "restore", message: "The restore could not be confirmed. Your local work is retained. Download a copy and reload the latest version." }
    : { kind: "failed", message: "Failed to save diagram. Your changes are retained; try again or download a local copy." };
}

export function useDiagramSave(document: BoardDocument, save: (capture: DiagramCheckpoint, source: "manual" | "autosave") => Promise<DiagramCheckpoint>) {
  const latest = useRef(document); latest.current = document;
  const saveRef = useRef(save); saveRef.current = save;
  const checkpoint = useRef<DiagramCheckpoint | null>(null);
  const generation = useRef(0);
  const submitted = useRef<DiagramCheckpoint | null>(null);
  const operationRef = useRef<Operation | null>(null);
  const [operation, setOperation] = useState<Operation | null>(null);
  const [failure, setFailure] = useState<SaveFailure | null>(null);
  const failureRef = useRef<SaveFailure | null>(null);
  const [baseline, setBaseline] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => { generation.current++; operationRef.current = null; clearTimeout(timer.current); }, []);

  const fail = useCallback((error: unknown, uncertainRestore = false) => {
    const next = saveFailure(error, uncertainRestore);
    failureRef.current = next; setFailure(next); setSaved(false);
  }, []);
  const adopt = useCallback((accepted: DiagramCheckpoint) => {
    if (checkpoint.current && compareRevision(accepted.revision, checkpoint.current.revision) < 0) return;
    submitted.current = null;
    checkpoint.current = structuredClone(accepted);
    setBaseline(canonicalDocument(accepted.document));
    failureRef.current = null; setFailure(null); setSaved(false); clearTimeout(timer.current);
  }, []);
  const advanceRevision = useCallback((revision: string) => {
    if (checkpoint.current && compareRevision(revision, checkpoint.current.revision) >= 0) checkpoint.current = { ...checkpoint.current, revision };
  }, []);
  const conflict = useCallback(() => {
    const next: SaveFailure = { kind: "conflict", message: "This diagram changed on the server. Your local edits are retained. Download a local copy, then reload the latest version to reapply them." };
    failureRef.current = next; setFailure(next); setSaved(false);
  }, []);
  const invalidate = useCallback(() => {
    generation.current++; operationRef.current = null; setOperation(null); clearTimeout(timer.current);
  }, []);
  const begin = useCallback((kind: Operation) => {
    if (operationRef.current) return null;
    const epoch = generation.current;
    operationRef.current = kind; setOperation(kind); setSaved(false); clearTimeout(timer.current);
    const current = () => generation.current === epoch && operationRef.current === kind;
    return { current, end: () => { if (current()) { operationRef.current = null; setOperation(null); } } };
  }, []);
  const doSave = useCallback(async (source: "manual" | "autosave") => {
    if (!checkpoint.current || operationRef.current || (failureRef.current && (source === "autosave" || failureRef.current.kind !== "failed"))) return "blocked";
    const capture: DiagramCheckpoint = { revision: checkpoint.current.revision,
      document: { title: latest.current.title, snapshot: persistableSnapshot(latest.current.snapshot) } };
    const canonical = canonicalDocument(capture.document);
    if (canonical === canonicalDocument(checkpoint.current.document)) return "unchanged";
    const op = begin("save");
    if (!op) return "blocked";
    submitted.current = structuredClone(capture);
    try {
      const accepted = await saveRef.current(capture, source);
      if (!op.current()) return "blocked";
      if (checkpoint.current && compareRevision(accepted.revision, checkpoint.current.revision) < 0) return "blocked";
      checkpoint.current = { revision: accepted.revision, document: capture.document };
      submitted.current = null;
      setBaseline(canonical); failureRef.current = null; setFailure(null);
      if (canonicalDocument(latest.current) === canonical) {
        setSaved(true);
        timer.current = setTimeout(() => setSaved(false), 2000);
      }
      return "saved";
    } catch (error) { if (op.current()) fail(error); return "failed"; }
    finally { op.end(); }
  }, [begin, fail]);
  const dirty = baseline !== null && canonicalDocument(document) !== baseline;
  return { checkpoint, submitted, operationRef, operation, failure, fail, conflict, invalidate, adopt, advanceRevision, begin, doSave, dirty,
    saveState: operation === "save" ? "saving" : saved && !dirty && !failure ? "saved" : "idle" };
}
